import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  getInvestigation,
  mapStoredToolCalls,
} from '../services/sreInvestigationService';
import type { InvestigationThought, StoredInvestigationRun } from '../types';
import { normalizeSreTimestamp } from '../utils/formatLocalDateTime';
import { ensureSreFontsLoaded } from '../styles/theme';
import InvestigationReport from './InvestigationReport';
import ExecutionTrace from './ExecutionTrace';
import SreWorkbenchBlur from './SreWorkbenchBlur';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import '../styles/sre-investigation.css';

export interface SreInvestigationHistoryPanelProps {
  open: boolean;
  onClose: () => void;
  ticketNumber: string;
  runs: StoredInvestigationRun[];
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  /** When true (multiple runs), keep the list view selected — do not auto-open a run. */
  preferListView?: boolean;
  /** Prefer opening the workbench for a run instead of inline detail. */
  onOpenRun?: (runId: string) => void;
}

function formatRunTimestamp(
  formatDateTime: (date: string | Date | null | undefined, includeSeconds?: boolean) => string,
  run: StoredInvestigationRun
): string {
  return formatDateTime(
    normalizeSreTimestamp(run.completedAt || run.startedAt),
    true
  );
}

const STATUS_PILL: Record<string, string> = {
  completed: 'bg-emerald-600 text-white',
  running: 'bg-primary-600 text-white',
  queued: 'bg-amber-500 text-white',
  failed: 'bg-red-600 text-white',
  cancelled: 'bg-slate-500 text-white',
};

/**
 * Pulse SRE–branded history drawer — list of persisted runs for a ticket.
 */
const SreInvestigationHistoryPanel: React.FC<
  SreInvestigationHistoryPanelProps
> = ({
  open,
  onClose,
  ticketNumber,
  runs,
  loading = false,
  error = null,
  onRefresh,
  preferListView = true,
  onOpenRun,
}) => {
  const { formatDateTime } = useDateFormatter();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<StoredInvestigationRun | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const listOnly = Boolean(onOpenRun);

  useEffect(() => {
    ensureSreFontsLoaded();
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }
    if (!preferListView && runs.length === 1 && !onOpenRun) {
      setSelectedId(runs[0].id);
    } else {
      setSelectedId(null);
      setDetail(null);
    }
  }, [open, preferListView, runs, onOpenRun]);

  useEffect(() => {
    if (!open || !selectedId || listOnly) {
      if (listOnly) {
        setDetail(null);
        setDetailError(null);
      }
      return;
    }

    const controller = new AbortController();
    setDetailLoading(true);
    setDetailError(null);

    void getInvestigation(selectedId, controller.signal)
      .then((run) => {
        if (!controller.signal.aborted) {
          setDetail(run);
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setDetail(null);
        setDetailError(
          err instanceof Error ? err.message : 'Failed to load investigation'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setDetailLoading(false);
        }
      });

    return () => controller.abort();
  }, [open, selectedId, listOnly]);

  const toolCalls = detail ? mapStoredToolCalls(detail) : [];
  const thoughts: InvestigationThought[] = Array.isArray(detail?.thoughts)
    ? detail!.thoughts!.flatMap((t, index) => {
        if (!t || typeof t !== 'object') return [];
        const text = typeof t.text === 'string' ? t.text : '';
        if (!text) return [];
        return [
          {
            text,
            ts: typeof t.ts === 'string' ? t.ts : '',
            seq: typeof t.seq === 'number' ? t.seq : index + 1,
            agent: typeof t.agent === 'string' ? t.agent : undefined,
          },
        ];
      })
    : [];

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[80]" onClose={onClose}>
        <SreWorkbenchBlur />
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/45 transition-opacity" aria-hidden />
        </Transition.Child>

        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-8 sm:pl-12">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-250"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-180"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel
              className={[
                'pointer-events-auto flex h-full w-screen flex-col',
                listOnly ? 'max-w-md' : 'max-w-3xl',
                "sre-wb bg-slate-50 font-['DM_Sans',ui-sans-serif,system-ui,sans-serif]",
                'shadow-2xl shadow-slate-900/20',
              ].join(' ')}
            >
                  {/* Header — aligned with Investigation workbench */}
                  <header className="relative overflow-hidden border-b border-slate-200 bg-white">
                    <div
                      className="pointer-events-none absolute inset-0 opacity-[0.55]"
                      style={{
                        backgroundImage:
                          'radial-gradient(circle at 12% 20%, rgba(27,78,255,0.12), transparent 42%), radial-gradient(circle at 88% 0%, rgba(201,180,1,0.14), transparent 36%)',
                      }}
                    />
                    <div className="relative flex items-start justify-between gap-3 px-5 py-4">
                      <div className="min-w-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600 text-white shadow-sm shadow-primary-600/25">
                            <ClockIcon className="h-5 w-5" />
                          </span>
                          <div>
                            <Dialog.Title className="text-base font-semibold tracking-tight text-slate-900">
                              SRE investigation history
                            </Dialog.Title>
                            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-400">
                              Pulse SRE
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-['IBM_Plex_Mono',ui-monospace,monospace] text-xs font-medium text-slate-700">
                            {ticketNumber || 'No ticket'}
                          </span>
                          {runs.length > 0 ? (
                            <span className="rounded-md bg-primary-50 px-2 py-0.5 text-[11px] font-semibold text-primary-700 ring-1 ring-inset ring-primary-100">
                              {runs.length} run{runs.length === 1 ? '' : 's'}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        {onRefresh ? (
                          <button
                            type="button"
                            onClick={onRefresh}
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                            aria-label="Refresh history"
                          >
                            <ArrowPathIcon
                              className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`}
                            />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={onClose}
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Close"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </header>

                  <div
                    className={[
                      'flex min-h-0 flex-1 flex-col overflow-hidden',
                      listOnly ? '' : 'lg:flex-row',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'min-h-0 flex-1 overflow-y-auto bg-white',
                        listOnly
                          ? ''
                          : 'max-h-56 shrink-0 border-b border-slate-100 lg:max-h-none lg:w-80 lg:flex-none lg:border-b-0 lg:border-r',
                      ].join(' ')}
                    >
                      {error ? (
                        <p className="px-5 py-4 text-sm text-red-700">{error}</p>
                      ) : null}
                      {loading && runs.length === 0 ? (
                        <p className="px-5 py-4 text-sm text-slate-500">
                          Loading history…
                        </p>
                      ) : null}
                      {!loading && !error && runs.length === 0 ? (
                        <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
                          <ShieldCheckIcon className="h-10 w-10 text-primary-200" />
                          <p className="text-sm font-medium text-slate-700">
                            No investigations yet
                          </p>
                          <p className="text-sm text-slate-500">
                            Runs for this ticket will appear here.
                          </p>
                        </div>
                      ) : null}

                      <ul className="divide-y divide-slate-100">
                        {runs.map((run) => {
                          const status = (run.status || 'unknown').toLowerCase();
                          const pill =
                            STATUS_PILL[status] || 'bg-slate-500 text-white';
                          const active = selectedId === run.id;
                          return (
                            <li key={run.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  if (onOpenRun) {
                                    onOpenRun(run.id);
                                    return;
                                  }
                                  setSelectedId(run.id);
                                }}
                                className={[
                                  'w-full px-5 py-4 text-left transition',
                                  active
                                    ? 'bg-primary-50/80'
                                    : 'hover:bg-slate-50',
                                ].join(' ')}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span
                                    className={[
                                      'inline-flex rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
                                      pill,
                                    ].join(' ')}
                                  >
                                    {status}
                                  </span>
                                  <span className="font-['IBM_Plex_Mono',ui-monospace,monospace] text-[10px] font-medium text-slate-400">
                                    {run.toolCallsCount ?? 0} tools
                                  </span>
                                </div>
                                <p className="mt-2 line-clamp-2 text-sm font-medium leading-snug text-slate-900">
                                  {run.rootCauseSummary ||
                                    run.useCaseId ||
                                    run.id}
                                </p>
                                <p className="mt-1.5 text-xs text-slate-500">
                                  {formatRunTimestamp(formatDateTime, run)}
                                </p>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    {!listOnly ? (
                      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-5">
                        {!selectedId ? (
                          <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 text-center text-sm text-slate-500">
                            <DocumentTextIcon className="h-8 w-8 text-primary-200" />
                            Select a run to view the report and execution
                            trace.
                          </div>
                        ) : null}

                        {selectedId && detailLoading ? (
                          <p className="text-sm text-slate-500">Loading run…</p>
                        ) : null}

                        {selectedId && detailError ? (
                          <p className="text-sm text-red-700">{detailError}</p>
                        ) : null}

                        {selectedId && detail && !detailLoading ? (
                          <div className="space-y-4">
                            <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs text-slate-600 shadow-sm">
                              <div className="font-['IBM_Plex_Mono',ui-monospace,monospace] text-[11px] text-slate-500">
                                {detail.id}
                              </div>
                              {detail.useCaseId ? (
                                <div className="mt-1">
                                  Use case:{' '}
                                  <span className="font-medium text-slate-800">
                                    {detail.useCaseId}
                                  </span>
                                </div>
                              ) : null}
                              {detail.confidence ? (
                                <div className="mt-0.5">
                                  Confidence:{' '}
                                  <span className="font-medium text-slate-800">
                                    {detail.confidence}
                                  </span>
                                </div>
                              ) : null}
                              {detail.errorMessage ? (
                                <div className="mt-1 text-red-700">
                                  {detail.errorMessage}
                                </div>
                              ) : null}
                            </div>

                            {detail.report ? (
                              <InvestigationReport report={detail.report} />
                            ) : detail.conclusionMarkdown ? (
                              <pre className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-sm">
                                {detail.conclusionMarkdown}
                              </pre>
                            ) : (
                              <p className="text-sm text-slate-500">
                                No report stored for this run.
                              </p>
                            )}

                            {(toolCalls.length > 0 ||
                              thoughts.length > 0 ||
                              (detail.toolCallsCount ?? 0) > 0) && (
                              <ExecutionTrace
                                toolCalls={toolCalls}
                                thoughts={thoughts}
                                isRunning={false}
                              />
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SreInvestigationHistoryPanel;
